import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { UserModel } from '@models/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/currentUser/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/currentUser/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/currentUser/FETCH_FAILURE'
const RESET_STORE = 'realbase/currentUser/RESET_STORE'

export type CurrentUserModuleState = {
  errors: string[],
  loaded: boolean,
  loading: boolean,
  result: {},
}

// Initial State
const initialState: CurrentUserModuleState = {
  errors: [],
  loaded: false,
  loading: false,
  result: {},
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(payload: Partial<UserModel> = {}) {
  return {
    type: FETCH_SUCCESS,
    payload,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function resetStore() {
  return { type: RESET_STORE }
}

// Action Creators
export function hydrateCurrentUser(user: Partial<UserModel>) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()

    if (user.id){
      const normalizedJson = normalize(user, Schemas.USER)
      dispatch(updateEntities(normalizedJson))
      // Need to update user as frontend permissions need to populate correctly
      dispatch(fetchSuccess({ ...state.entities.users[user.id], ...user } as UserModel))

      return { success: true as const, data: user }
    }
    return { success: false as const, errors: ['Could not hydrate user without ID'] }
  }
}

export function loadCurrentUser(options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    // Set Loading
    dispatch(fetchRequest())

    const promise = api('/users/me.json', options)
      .then(({ data }: { data: UserModel }) => {
        const normalizedJson = normalize(data, Schemas.USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(data))

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function clearStore() {
  return (dispatch: AppDispatch) => {
    dispatch(resetStore())

    return Promise.resolve()
  }
}

// Reducer
export default function reducer(
  state: CurrentUserModuleState = initialState,
  action: Action = { type: '' },
): CurrentUserModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
        // @ts-expect-error TS2339
        result: action.payload,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case RESET_STORE:
      // @ts-expect-error TS2739
      return {}
    default:
      return state
  }
}
